import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import MobileNavDock from "./components/MobileNavDock";
import useScrollToTop from "./hooks/useScrollToTop";
import usePageTracking from "./hooks/usePageTracking";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import SiteStructuredData from "./components/SiteStructuredData";
import { EVENT_UPDATES_MODAL_STORAGE_KEY } from "./constants/eventUpdatesModal";
/* SHELVED MEDIA — restore MediaHubRouteFallback / MaybeMediaClerk / WaveWelcomeModal when bringing Media Hub back
import { MediaHubRouteFallback } from "./components/MediaHubRouteFallback";
import MaybeMediaClerk from "./components/MaybeMediaClerk";
import { shouldShowMediaHubFeedSkeleton } from "./utils/mediaHubRouteFallback";
import { WAVE_WELCOME_MODAL_STORAGE_KEY } from "./constants/waveWelcomeModal";
*/
// Eager: homepage is the critical landing route — lazy MainPage added an extra network hop.
import MainPage from "./pages/MainPage";

// Lazy-loaded pages / chrome for smaller initial homepage bundle
const TicketModal = lazy(() => import("./components/TicketModal"));
const CookieConsentBanner = lazy(() => import("./components/CookieConsent"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const EventUpdatesModal = lazy(() => import("./components/EventUpdatesModal"));
const EarlyBirdAccessTab = lazy(() => import("./components/EarlyBirdAccessTab"));
/* SHELVED MEDIA
const WaveWelcomeModal = lazy(() =>
  import("./components/WaveWelcomeModal").then((m) => ({ default: m.WaveWelcomeModal })),
);
*/
// RETIRED: Side event page decommissioned — route and nav links removed.
// const SiderEvent = lazy(() => import("./pages/sider_event"));
const Sydney2026 = lazy(() => import("./pages/sydney2026"));
// const SponsorshipWOI2025 = lazy(() => import("./pages/sponsorship_woi_2025"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Terms = lazy(() => import("./pages/Terms"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const Privacy = lazy(() => import("./pages/Privacy"));
const GalleryResources = lazy(() => import("./pages/GalleryResources"));
const Team = lazy(() => import("./pages/Team"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Events = lazy(() => import("./pages/Events"));
const Careers = lazy(() => import("./pages/Careers"));
const CareerJob = lazy(() => import("./pages/CareerJob"));
const SpeakerHub = lazy(() => import("./pages/SpeakerHub"));
const SpeakerHubEvent = lazy(() => import("./pages/SpeakerHubEvent"));
const Display2026 = lazy(() => import("./pages/Display2026"));
const Branding = lazy(() => import("./pages/Branding"));
const Faq = lazy(() => import("./pages/Faq"));
const Attendance = lazy(() => import("./pages/Attendance"));
const EventTickets = lazy(() => import("./pages/EventTickets"));
const XRPLondon = lazy(() => import("./pages/XRPLondon"));
const XRPSydney = lazy(() => import("./pages/XRPSydney"));
const XRPAustralia2026Collage = lazy(() => import("./pages/XRPAustralia2026Collage"));
/* SHELVED MEDIA — restore lazy page imports when bringing Media Hub back
const LegacyAuthorAccess = lazy(() => import("./pages/LegacyAuthorAccess"));
const Media = lazy(() => import("./pages/Media"));
const MediaAccount = lazy(() => import("./pages/MediaAccount"));
const MediaSubmit = lazy(() => import("./pages/MediaSubmit"));
const MediaDrafts = lazy(() => import("./pages/MediaDrafts"));
const MediaReview = lazy(() => import("./pages/MediaReview"));
const MediaReviewArticle = lazy(() => import("./pages/MediaReviewArticle"));
const MediaSubmissionPreview = lazy(() => import("./pages/MediaSubmissionPreview"));
const MediaArticleGuidelines = lazy(() => import("./pages/MediaArticleGuidelines"));
*/
const ColorPalette = lazy(() => import("./pages/ColorPalette"));
const AIPage = lazy(() => import("./pages/AIPage"));

// ScrollToTop component
const ScrollToTop = () => {
  useScrollToTop();
  return null;
};

// PageTracking component - must be inside Router
const PageTracking = () => {
  usePageTracking();
  return null;
};

// ErrorBoundary class component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong. Please try refreshing the page.</div>;
    }
    return this.props.children;
  }
}

/** Early bird side tab + optional one-time homepage opener. */
function EventUpdatesModalGate() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const isHome = pathname === "/" || pathname === "/home";

  useEffect(() => {
    if (!isHome) return;
    try {
      if (globalThis.localStorage?.getItem(EVENT_UPDATES_MODAL_STORAGE_KEY)) return;
    } catch {
      return;
    }
    const id = window.requestAnimationFrame(() => setOpen(true));
    return () => window.cancelAnimationFrame(id);
  }, [isHome]);

  const dismiss = useCallback(() => {
    try {
      globalThis.localStorage?.setItem(EVENT_UPDATES_MODAL_STORAGE_KEY, "1");
    } catch {
      /* Private mode — still close */
    }
    setOpen(false);
  }, []);

  const openModal = useCallback(() => setOpen(true), []);

  return (
    <Suspense fallback={null}>
      <EarlyBirdAccessTab onOpen={openModal} hidden={open} />
      <EventUpdatesModal isOpen={open} onDismiss={dismiss} />
    </Suspense>
  );
}

// Minimal fallback while lazy route chunks load
function PageLoadFallback() {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
      <div
        style={{
          padding: "1.1rem 1.35rem",
          borderRadius: 20,
          backgroundColor: "rgba(13, 69, 117, 0.06)",
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            border: "3px solid #0d4575",
            borderTopColor: "transparent",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
      </div>
    </div>
  );
}

// Conditional TicketModal — chunk loads only when opened
function ConditionalTicketModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const location = useLocation();
  const excludedPaths = ['/blog', '/home'];
  const shouldShow = !excludedPaths.some(path => location.pathname === path || location.pathname.startsWith(path + '/'));

  if (!shouldShow || !isOpen) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <TicketModal isOpen={isOpen} onClose={onClose} />
    </Suspense>
  );
}

/** Cookie banner + react-cookie-consent stay off the homepage critical JS path. */
function DeferredCookieConsent() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const enable = () => setReady(true);
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(enable, { timeout: 4000 });
    } else {
      timeoutId = setTimeout(enable, 2000);
    }
    return () => {
      if (idleId !== undefined && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, []);

  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <CookieConsentBanner />
    </Suspense>
  );
}

const SPEED_INSIGHTS_DEBUG =
  import.meta.env.DEV || import.meta.env.VITE_SPEED_INSIGHTS_DEBUG === "true";

function normalizeSpeedInsightsRoute(pathname: string): string {
  if (pathname.startsWith("/blog/")) {
    return "/blog/:articleUrl";
  }

  if (pathname.startsWith("/careers/")) {
    return "/careers/:jobSlug";
  }

  if (pathname.startsWith("/media/account/preview/")) {
    return "/media/account/preview/:previewSlug";
  }

  if (pathname.startsWith("/media/review/item/")) {
    return "/media/review/item/:articleSlug";
  }

  if (
    pathname.startsWith("/media/") &&
    !pathname.startsWith("/media/account") &&
    !pathname.startsWith("/media/submit") &&
    !pathname.startsWith("/media/guidelines") &&
    !pathname.startsWith("/media/review")
  ) {
    return "/media/:articleSlug";
  }

  if (pathname.startsWith("/author/")) {
    return "/author/*";
  }

  return pathname;
}

function SpeedInsightsTracker() {
  const { pathname } = useLocation();
  const route = normalizeSpeedInsightsRoute(pathname);

  return (
    <SpeedInsights
      route={route}
      debug={SPEED_INSIGHTS_DEBUG}
      beforeSend={(event: any) => {
        if (event && (event.route === undefined || event.route === null || event.route === "")) {
          event.route = route;
        }
        if (SPEED_INSIGHTS_DEBUG) {
          console.debug("[SpeedInsights]", {
            metric: event?.name,
            value: event?.value,
            rating: event?.rating,
            route: event?.route ?? route,
            url: event?.url,
          });
        }

        return event;
      }}
    />
  );
}

// Main App component
function App() {
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  // matchMedia avoids forced reflow from reading innerWidth after React commits DOM.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches,
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Use BrowserRouter for clean URLs
  const RouterComponent = BrowserRouter;

  const routerTree = (
      <RouterComponent future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* SHELVED MEDIA — wrap with <MaybeMediaClerk> again when restoring */}
        <ScrollToTop />
        <PageTracking />
        <Analytics />
        <SpeedInsightsTracker />
        <DeferredCookieConsent />
        <SiteStructuredData />
        <Suspense fallback={<PageLoadFallback />}>
          <Routes>
            <Route
              path="/"
              element={<MainPage isMobile={isMobile} onTicketClick={() => setIsTicketModalOpen(true)} />}
            />
            <Route
              path="/home"
              element={<MainPage isMobile={isMobile} onTicketClick={() => setIsTicketModalOpen(true)} />}
            />
            <Route path="/sydney2026" element={<Sydney2026 />} />
            {/* XRP Australia 2026 sponsorship deck — disabled */}
            {/* <Route path="/sponsorship" element={<SponsorshipWOI2025 />} /> */}
            {/* RETIRED: /side-event page decommissioned */}
            {/* <Route path="/side-event" element={<SiderEvent isMobile={isMobile} />} /> */}
            <Route path="/register" element={<Navigate to="/#footer-register" replace />} />
            {/* SHELVED PROGRAMS — restore when bringing Programs back
            <Route path="/programs" element={<Navigate to="/#footer-register" replace />} />
            */}
            <Route path="/programs/*" element={<Navigate to="/" replace />} />
            <Route path="/programs" element={<Navigate to="/" replace />} />
            <Route path="/builders-hub" element={<Navigate to="/" replace />} />
            <Route path="/xrp-london" element={<XRPLondon />} />
            <Route path="/xrp-australia" element={<XRPSydney />} />
            <Route path="/xrplondon" element={<Navigate to="/xrp-london" replace />} />
            <Route path="/xrpaustralia" element={<Navigate to="/xrp-australia" replace />} />
            <Route path="/xrpsydney" element={<Navigate to="/xrp-australia" replace />} />
            <Route path="/xrpsydney/*" element={<Navigate to="/xrp-australia" replace />} />
            <Route path="/blog" element={<Blog onTicketClick={() => setIsTicketModalOpen(true)} />} />
            <Route path="/blog/:articleUrl" element={<BlogArticle onTicketClick={() => setIsTicketModalOpen(true)} />} />
            {/* SHELVED MEDIA — restore routes below; temporary redirects keep bookmarks safe
            <Route path="/media/account/preview/:previewSlug" element={<MediaSubmissionPreview />} />
            <Route path="/media/account" element={<MediaAccount />} />
            <Route path="/media/submit/drafts" element={<MediaDrafts />} />
            <Route path="/media/submit" element={<MediaSubmit />} />
            <Route path="/media/guidelines" element={<MediaArticleGuidelines />} />
            <Route path="/media/review/item/:articleSlug" element={<MediaReviewArticle />} />
            <Route path="/media/review" element={<MediaReview />} />
            <Route path="/media" element={<Media />} />
            <Route path="/media/:articleSlug" element={<Media />} />
            <Route path="/author/*" element={<LegacyAuthorAccess />} />
            */}
            <Route path="/media/*" element={<Navigate to="/" replace />} />
            <Route path="/media" element={<Navigate to="/" replace />} />
            <Route path="/author/*" element={<Navigate to="/" replace />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/gallery_resources" element={<GalleryResources />} />
            <Route path="/resources" element={<Blog onTicketClick={() => setIsTicketModalOpen(true)} />} />
            <Route path="/team" element={<Team onTicketClick={() => setIsTicketModalOpen(true)} />} />
            <Route path="/about-us" element={<Navigate to="/about" replace />} />
            <Route path="/partners" element={<Team onTicketClick={() => setIsTicketModalOpen(true)} />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/faq" element={<Faq onTicketClick={() => setIsTicketModalOpen(true)} />} />
            <Route path="/events" element={<Events />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:jobSlug" element={<CareerJob />} />
            <Route path="/speakerhub" element={<SpeakerHub />} />
            <Route path="/speakerhub-sydney" element={<SpeakerHubEvent eventKey="sydney" />} />
            <Route path="/speakerhub-london" element={<SpeakerHubEvent eventKey="london" />} />
            <Route path="/display2026" element={<Display2026 />} />
            <Route path="/branding" element={<Branding />} />
            <Route path="/color-palette" element={<ColorPalette />} />
            <Route path="/ai" element={<AIPage />} />
            <Route path="/a7k9m2p4" element={<Attendance />} />
            <Route path="/event" element={<EventTickets />} />
            <Route path="/kaleido" element={<Navigate to="/" replace />} />
            <Route
              path="/xrp-gold-coast"
              element={
                <ComingSoon
                  title="XRP Gold Coast"
                  description="We are preparing the full event experience and details. Register to get early updates, speaker announcements, and next-step information."
                  primaryCtaLabel="Get early updates"
                  primaryCtaTo="/xrp-gold-coast#footer-register"
                  secondaryCtaLabel="Browse events"
                  secondaryCtaTo="/events"
                  relatedLinks={[
                    { label: "Events", to: "/events" },
                    { label: "Blog", to: "/blog" },
                  ]}
                />
              }
            />
            <Route
              path="/bridging-blockchains"
              element={
                <ComingSoon
                  title="Bridging Blockchains"
                  description="A high-trust space for leaders to collaborate on real deployment outcomes. Join the newsletter and register for the next round of announcements."
                  primaryCtaLabel="Register for updates"
                  primaryCtaTo="/bridging-blockchains#footer-register"
                  secondaryCtaLabel="Browse events"
                  secondaryCtaTo="/events"
                  relatedLinks={[
                    { label: "Events", to: "/events" },
                    { label: "Company", to: "/team" },
                  ]}
                />
              }
            />
            <Route
              path="/ledger-lounge"
              element={
                <ComingSoon
                  title="The Ledger Lounge"
                  description="We are finalizing details for the next edition. Register to stay informed and discover how the event connects institutional teams, builders, and ecosystem stakeholders."
                  primaryCtaLabel="Stay informed"
                  primaryCtaTo="/ledger-lounge#footer-register"
                  secondaryCtaLabel="View all events"
                  secondaryCtaTo="/events"
                  relatedLinks={[
                    { label: "Events", to: "/events" },
                    { label: "About", to: "/about" },
                  ]}
                />
              }
            />
            <Route
              path="/xao-dao"
              element={
                <ComingSoon
                  title="XAO DAO"
                  description="Learn more about governance and ecosystem initiatives as we publish the complete information hub. Register for announcements or browse upcoming events."
                  primaryCtaLabel="Browse events"
                  primaryCtaTo="/events"
                  secondaryCtaLabel="Register"
                  secondaryCtaTo="/xao-dao#footer-register"
                  relatedLinks={[
                    { label: "Events", to: "/events" },
                    { label: "Blog", to: "/blog" },
                  ]}
                />
              }
            />
            <Route path="/gallery" element={<XRPAustralia2026Collage />} />
            <Route path="/xrp-australia-2026-collage" element={<XRPAustralia2026Collage />} />
          </Routes>
        </Suspense>
        <MobileNavDock />
        <ConditionalTicketModal isOpen={isTicketModalOpen} onClose={() => setIsTicketModalOpen(false)} />
        <EventUpdatesModalGate />
        </RouterComponent>
  );

  return <ErrorBoundary>{routerTree}</ErrorBoundary>;
}

export default App;